/**
 * Pure validators for the customer-organization form (H-3b).
 *
 * Kept framework-free and side-effect-free so the create/detail pages can run
 * them against FORM STATE (not the API response) on every render, per the
 * house rule on edit-form validation.
 */

// Pragmatic single-@ email shape. We deliberately avoid an exhaustive RFC 5322
// regex — the goal is to catch obvious typos (missing @, missing domain dot),
// not to be the authority on deliverability, which only sending can confirm.
// The domain is matched as dot-separated dot-free labels so the pattern stays
// linear (no overlapping quantifiers → no super-linear backtracking, S5852).
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

/**
 * Basic billing-email format check. Empty is handled by the caller (the field
 * is optional); this only judges a non-empty value.
 */
export const isValidBillingEmail = (value: string): boolean =>
    EMAIL_RE.test(value.trim());

// EU-style VAT id: a 2-letter country prefix followed by 2–12 alphanumerics
// (e.g. "DE123456789", "ATU12345678"). Case-insensitive; we normalise away
// spaces first since users often paste "DE 123 456 789". This is a format
// gate, not a checksum — VIES validation is a backend concern.
const VAT_RE = /^[A-Z]{2}[A-Z0-9]{2,12}$/;

/**
 * Basic VAT-id format check. Empty is handled by the caller (the field is
 * optional); this only judges a non-empty value.
 */
export const isValidVatId = (value: string): boolean =>
    VAT_RE.test(value.replace(/\s+/g, '').toUpperCase());

// The backend stores billing_country as VARCHAR(2) and rejects anything but a
// 2-letter code ("billingAddress.country must be a 2-letter ISO code"), so the
// form must never submit a free-text country name.
const COUNTRY_CODE_RE = /^[A-Z]{2}$/i;

/**
 * ISO 3166-1 alpha-2 shape check. Empty is handled by the caller (the field
 * is optional); this only judges a non-empty value. Format gate only — the
 * country picker constrains the value to real codes, this catches stale
 * free-text values from before the picker existed.
 */
export const isValidCountryCode = (value: string): boolean =>
    COUNTRY_CODE_RE.test(value.trim());
